import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, FileDown, Send, Loader2, X, CheckCircle2, Pencil, Mail, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const inp = "w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30";
const money = (v, c = "USD") => `${c} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function LineEditor({ title, lines, setLines }) {
  const set = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2"><h4 className="font-semibold text-forest text-sm">{title}</h4>
        <button onClick={() => setLines([...lines, { label: "", amount: 0 }])} className="text-xs text-rose font-semibold inline-flex items-center gap-1"><Plus size={13} /> Add line</button></div>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex gap-2">
            <input value={l.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`${title.toLowerCase()}-label-${i}`} className={`${inp} flex-1`} placeholder="Description" />
            <input type="number" value={l.amount} onChange={(e) => set(i, "amount", Number(e.target.value))} data-testid={`${title.toLowerCase()}-amount-${i}`} className="w-28 rounded-lg border border-stone-200 px-3 py-2 text-sm" placeholder="0.00" />
            <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="p-1.5 text-red-500"><Trash2 size={15} /></button>
          </div>
        ))}
        {lines.length === 0 && <p className="text-xs text-stone-400">No {title.toLowerCase()} yet.</p>}
      </div>
    </div>
  );
}

function PayslipEditor({ staff, payslip, onClose, onSaved }) {
  const editing = !!payslip;
  const [period, setPeriod] = useState(payslip?.period_label || new Date().toLocaleString("en", { month: "long", year: "numeric" }));
  const [start, setStart] = useState(payslip?.period_start || "");
  const [end, setEnd] = useState(payslip?.period_end || "");
  const [earnings, setEarnings] = useState(payslip?.earnings || []);
  const [deductions, setDeductions] = useState(payslip?.deductions || []);
  const [notes, setNotes] = useState(payslip?.notes || "");
  const [saving, setSaving] = useState(false);
  const cur = payslip?.currency || staff?.currency || "USD";
  const gross = earnings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totDed = deductions.reduce((s, d) => s + Number(d.amount || 0), 0);

  const save = async () => {
    setSaving(true);
    const body = { staff_id: staff?.id || payslip?.staff_id, period_label: period, period_start: start, period_end: end, earnings, deductions, notes };
    try {
      if (editing) await api.patch(`/admin/payroll/payslips/${payslip.id}`, body);
      else await api.post("/admin/payroll/payslips", body);
      toast.success(editing ? "Payslip updated" : "Payslip created"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="payslip-editor">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl p-5 my-0 sm:my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-forest">{editing ? "Edit payslip" : "New payslip"} — {staff?.name || payslip?.staff_name}</h3><button onClick={onClose}><X size={20} className="text-stone-400" /></button></div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div><label className="text-xs text-stone-500">Pay period label</label><input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="payslip-period" className={inp} /></div>
          <div><label className="text-xs text-stone-500">Period start</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="payslip-start" className={inp} /></div>
          <div><label className="text-xs text-stone-500">Period end</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="payslip-end" className={inp} /></div>
        </div>
        <LineEditor title="Earnings" lines={earnings} setLines={setEarnings} />
        <LineEditor title="Deductions" lines={deductions} setLines={setDeductions} />
        <div className="flex justify-between items-center bg-sand rounded-lg px-4 py-3 mb-4">
          <div className="text-sm text-stone-600">Gross {money(gross, cur)} − Deductions {money(totDed, cur)}</div>
          <div className="font-serif text-xl text-forest">Net {money(gross - totDed, cur)}</div>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} rows={2} placeholder="Notes (optional)" />
        <p className="text-xs text-stone-400 mt-2">Cleaning productivity is calculated automatically from submitted worksheets within the pay period.</p>
        <button onClick={save} disabled={saving} data-testid="payslip-save" className="btn-primary w-full justify-center mt-4 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : "Save payslip"}</button>
      </div>
    </div>
  );
}

function PayrollSummary() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/admin/payroll/summary?start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => {}), [start, end]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    try { const r = await api.get(`/admin/payroll/summary.pdf?start=${start}&end=${end}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "rose-air-payroll-summary.pdf"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch { toast.error("PDF unavailable"); }
  };
  const cards = [["Payslips", data?.count ?? 0], ["Total gross", money(data?.total_gross)], ["Total deductions", money(data?.total_deductions)], ["Total net", money(data?.total_net)]];
  return (
    <div className="card-lux p-4 mb-4" data-testid="payroll-summary">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><BarChart3 size={18} className="text-rose" /><h3 className="font-serif text-lg text-forest">Payroll summary</h3></div>
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-[11px] text-stone-500">From</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="summary-start" className={inp} /></div>
          <div><label className="text-[11px] text-stone-500">To</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="summary-end" className={inp} /></div>
          <button onClick={download} data-testid="summary-pdf" className="btn-primary text-sm"><FileDown size={15} /> Summary PDF</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([l, v]) => <div key={l} className="bg-sand/50 rounded-lg p-3"><div className="text-xs text-stone-500">{l}</div><div className="font-serif text-xl text-forest mt-1">{v}</div></div>)}
      </div>
    </div>
  );
}

export default function PayrollAdmin() {
  const [staff, setStaff] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [filterStaff, setFilterStaff] = useState("");
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/service-staff"),
      api.get(`/admin/payroll/payslips${filterStaff ? `?staff_id=${filterStaff}` : ""}`),
    ]).then(([s, p]) => { setStaff(s.data); setPayslips(p.data); }).catch(() => {}).finally(() => setLoading(false));
  }, [filterStaff]);
  useEffect(() => { load(); }, [load]);

  const finalize = async (p) => { await api.post(`/admin/payroll/payslips/${p.id}/finalize`); toast.success("Payslip finalized — staff can now download it"); load(); };
  const del = async (p) => { if (!window.confirm(`Delete payslip for ${p.staff_name} (${p.period_label})?`)) return; await api.delete(`/admin/payroll/payslips/${p.id}`); toast.success("Deleted"); load(); };
  const download = async (p) => {
    try { const r = await api.get(`/admin/payroll/payslips/${p.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `payslip-${p.staff_name}-${p.period_label}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch { toast.error("PDF unavailable"); }
  };
  const whatsapp = (p) => {
    const text = encodeURIComponent(`Hi ${p.staff_name}, your payslip for ${p.period_label} is ready. Net pay: ${money(p.net_pay, p.currency)}. Please find the PDF attached.`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const emailPayslip = async (p) => {
    try { const { data } = await api.post(`/admin/payroll/payslips/${p.id}/email`); toast.success(`Payslip emailed to ${data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const autoDraft = async () => {
    if (!window.confirm("Auto-draft payslips for the previous month for all active staff? Existing payslips for that month are skipped.")) return;
    try { const { data } = await api.post("/admin/payroll/draft-month"); toast.success(`${data.drafted} payslip(s) drafted for ${data.period}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="payroll-admin">
      <PayrollSummary />
      <div className="card-lux p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-stone-500">Filter by staff</label>
          <select value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)} data-testid="payroll-filter" className={inp}>
            <option value="">All staff</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.username}</option>)}
          </select></div>
        <div className="flex-1" />
        <button onClick={autoDraft} data-testid="payroll-autodraft" className="text-sm px-4 py-2 rounded-full bg-sand text-forest font-semibold inline-flex items-center gap-2"><CheckCircle2 size={15} /> Auto-draft last month</button>
        <select onChange={(e) => { const s = staff.find((x) => x.id === e.target.value); if (s) setEditor({ staff: s }); e.target.value = ""; }} data-testid="payroll-new-select" className={inp} style={{ maxWidth: 260 }}>
          <option value="">+ New payslip for…</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.username}</option>)}
        </select>
      </div>

      {loading ? <Loader2 className="animate-spin text-rose mx-auto my-8" /> : (
        <div className="card-lux overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payroll-table">
            <thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-3">Staff</th><th className="text-left p-3">Period</th><th className="text-left p-3">Net pay</th><th className="text-left p-3">Productivity</th><th className="text-left p-3">Status</th><th className="text-left p-3">Actions</th></tr></thead>
            <tbody>
              {payslips.map((p) => (
                <tr key={p.id} className="border-t border-stone-100" data-testid={`payslip-row-${p.id}`}>
                  <td className="p-3 font-medium text-forest">{p.staff_name}<div className="text-xs text-stone-400 font-mono">{p.payroll_number}</div></td>
                  <td className="p-3">{p.period_label}</td>
                  <td className="p-3 font-semibold">{money(p.net_pay, p.currency)}</td>
                  <td className="p-3 text-xs">{p.productivity?.completed || 0}/{p.productivity?.worksheets || 0} · {p.productivity?.avg_completion || 0}%</td>
                  <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${p.status === "finalized" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                  <td className="p-3"><div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setEditor({ payslip: p, staff: staff.find((s) => s.id === p.staff_id) })} data-testid={`payslip-edit-${p.id}`} title="Edit" className="p-1.5 rounded bg-stone-100"><Pencil size={13} /></button>
                    <button onClick={() => download(p)} data-testid={`payslip-download-${p.id}`} title="Download PDF" className="p-1.5 rounded bg-stone-100"><FileDown size={13} /></button>
                    <button onClick={() => whatsapp(p)} data-testid={`payslip-whatsapp-${p.id}`} title="WhatsApp" className="p-1.5 rounded bg-green-50 text-green-700"><Send size={13} /></button>
                    {p.status === "finalized" && <button onClick={() => emailPayslip(p)} data-testid={`payslip-email-${p.id}`} title="Email payslip to staff" className="p-1.5 rounded bg-blue-50 text-blue-700"><Mail size={13} /></button>}
                    {p.status !== "finalized" && <button onClick={() => finalize(p)} data-testid={`payslip-finalize-${p.id}`} title="Finalize" className="p-1.5 rounded bg-forest text-cream"><CheckCircle2 size={13} /></button>}
                    <button onClick={() => del(p)} title="Delete" className="p-1.5 rounded bg-red-50 text-red-600"><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
              {payslips.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400">No payslips yet. Use "+ New payslip for…" above.</td></tr>}
            </tbody>
          </table>
        </div></div>
      )}
      {editor && <PayslipEditor staff={editor.staff} payslip={editor.payslip} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
